// src/pages/subscription/AddOnListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionAddon, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { AddOnFormDialog } from '@/features/subscription/AddOnFormDialog';
import { formatMoney, humanizeEnum } from '@/features/subscription/format';

export const AddOnListPage = observer(() => {
  const store = useSubscriptionAddon();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    store.ListAddOns({
      pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
      sort: [], date_range: {} as any, filters: {} as any,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('common.confirmDelete'))) return;
    setDeleting(id);
    try {
      await store.DeleteAddOn(id);
      store.ListAddOns({
        pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
        sort: [], date_range: {} as any, filters: {} as any,
      });
    } catch {
      // Error handled by store
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Add-Ons</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">Add-Ons</h1>
        </div>
        <Button onClick={() => setShowCreate(true)}>Create Add-On</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Billing Cycle</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Public</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={8} message="No add-ons found" />
            ) : (
              store.items.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      <Link to={`/subscriptions/addons/${itemId}`} className="text-primary hover:underline">
                        {String(item.name ?? '')}
                      </Link>
                    </TableCell>
                    <TableCell>{String(item.slug ?? '--')}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" size="sm">{humanizeEnum(item.addon_type, 'ADDON_TYPE_')}</Badge>
                    </TableCell>
                    <TableCell>{formatMoney(item.price_amount, item.currency)}</TableCell>
                    <TableCell>{humanizeEnum(item.billing_cycle, 'BILLING_CYCLE_')}</TableCell>
                    <TableCell>
                      {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
                    </TableCell>
                    <TableCell>
                      {item.is_public ? <Badge variant="primary" size="sm">Public</Badge> : <Badge variant="secondary" size="sm">Private</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/subscriptions/addons/${itemId}`}><Button variant="ghost" size="sm">View</Button></Link>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(itemId)} disabled={deleting === itemId}>
                          {deleting === itemId ? '...' : t('common.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <AddOnFormDialog
        open={showCreate}
        addon={null}
        onClose={() => setShowCreate(false)}
        onSaved={() => {
          store.ListAddOns({
            pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
            sort: [], date_range: {} as any, filters: {} as any,
          });
        }}
      />
    </div>
  );
});

export default AddOnListPage;
